import React, { useState, useEffect } from 'react';
import { useSettingsPermissions } from '../../hooks/useSettingsPermissions';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import taxService from '../../services/taxService';
import { ITax } from '../../types/tax';

const TaxList: React.FC = () => {
  const theme = useTheme();
  const { hasTaxPermission } = useSettingsPermissions();
  const navigate = useNavigate();
  const [taxes, setTaxes] = useState<ITax[]>([]);
  const [filteredTaxes, setFilteredTaxes] = useState<ITax[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [taxToDelete, setTaxToDelete] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });

  // Fetch taxes on component mount
  useEffect(() => {
    fetchTaxes();
  }, []);

  // Filter taxes when search query changes
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredTaxes(taxes);
    } else {
      const lowercasedQuery = searchQuery.toLowerCase();
      const filtered = taxes.filter(tax => 
        tax.name.toLowerCase().includes(lowercasedQuery) || 
        tax.description?.toLowerCase().includes(lowercasedQuery) ||
        tax.rate.toString().includes(lowercasedQuery)
      );
      setFilteredTaxes(filtered);
    }
  }, [searchQuery, taxes]);

  const fetchTaxes = async () => {
    setLoading(true);
    try {
      const taxData = await taxService.getAllTaxes();
      setTaxes(taxData);
      setFilteredTaxes(taxData);
      setError(null);
    } catch (err) {
      console.error('Error fetching taxes:', err);
      setError('حدث خطأ أثناء جلب بيانات الضرائب');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTax = () => {
    if (!hasTaxPermission('add')) {
      setSnackbar({
        open: true,
        message: 'ليس لديك صلاحية إضافة ضريبة جديدة',
        severity: 'error'
      });
      return;
    }
    
    navigate('/settings/taxes/new');
  };

  const handleEditTax = (id: string) => {
    if (!hasTaxPermission('edit')) {
      setSnackbar({
        open: true,
        message: 'ليس لديك صلاحية تعديل الضرائب',
        severity: 'error'
      });
      return;
    }
    
    navigate(`/settings/taxes/edit/${id}`);
  };

  const handleToggleStatus = async (id: string) => {
    if (!hasTaxPermission('toggle_status')) {
      setSnackbar({
        open: true,
        message: 'ليس لديك صلاحية تنشيط/إلغاء تنشيط الضرائب',
        severity: 'error'
      });
      return;
    }
    
    try {
      await taxService.toggleTaxStatus(id);
      // Update the local state
      setTaxes(prevTaxes => 
        prevTaxes.map(tax => 
          tax._id === id ? { ...tax, isActive: !tax.isActive } : tax
        )
      );
      setSnackbar({
        open: true,
        message: 'تم تحديث حالة الضريبة بنجاح',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error toggling tax status:', err);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحديث حالة الضريبة',
        severity: 'error'
      });
    }
  };

  const openDeleteConfirm = (id: string) => {
    if (!hasTaxPermission('delete')) {
      setSnackbar({
        open: true,
        message: 'ليس لديك صلاحية حذف الضرائب',
        severity: 'error'
      });
      return;
    }
    
    setTaxToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setTaxToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!taxToDelete) return;
    
    try {
      await taxService.deleteTax(taxToDelete);
      setTaxes(prevTaxes => prevTaxes.filter(tax => tax._id !== taxToDelete));
      setSnackbar({
        open: true,
        message: 'تم حذف الضريبة بنجاح',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error deleting tax:', err);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حذف الضريبة',
        severity: 'error'
      });
    } finally {
      setDeleteConfirmOpen(false);
      setTaxToDelete(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
          إدارة الضرائب
        </Typography>
        {hasTaxPermission('add') && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddTax}
          >
            إضافة ضريبة جديدة
          </Button>
        )}
      </Box>

      <Card sx={{ mb: 3, boxShadow: '0 0 10px rgba(0,0,0,0.1)' }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="البحث عن ضريبة..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                    <TableCell>اسم الضريبة</TableCell>
                    <TableCell>معدل الضريبة</TableCell>
                    <TableCell>الوصف</TableCell>
                    <TableCell>الحالة</TableCell>
                    <TableCell align="center">الإجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTaxes.length > 0 ? (
                    filteredTaxes.map((tax) => (
                      <TableRow key={tax._id} hover>
                        <TableCell>{tax.name}</TableCell>
                        <TableCell>{tax.rate}%</TableCell>
                        <TableCell>{tax.description || 'لا يوجد وصف'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={tax.isActive ? 'نشط' : 'غير نشط'}
                            color={tax.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            {hasTaxPermission('edit') && (
                              <Tooltip title="تعديل">
                                <IconButton
                                  color="primary"
                                  onClick={() => handleEditTax(tax._id)}
                                  size="small"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            {hasTaxPermission('toggle_status') && (
                              <Tooltip title={tax.isActive ? 'إلغاء تنشيط' : 'تنشيط'}>
                                <IconButton
                                  color={tax.isActive ? 'success' : 'default'}
                                  onClick={() => handleToggleStatus(tax._id)}
                                  size="small"
                                >
                                  {tax.isActive ?
                                    <ToggleOnIcon fontSize="small" /> :
                                    <ToggleOffIcon fontSize="small" />
                                  }
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            {hasTaxPermission('delete') && (
                              <Tooltip title="حذف">
                                <IconButton
                                  color="error"
                                  onClick={() => openDeleteConfirm(tax._id)}
                                  size="small"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body1" sx={{ py: 2 }}>
                          {searchQuery ? 'لا توجد نتائج للبحث' : 'لا توجد ضرائب مضافة'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد أنك تريد حذف هذه الضريبة؟ لا يمكن التراجع عن هذا الإجراء.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            إلغاء
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            حذف
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TaxList;